/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import CheckableExpandableSearchFilterGroup, {
  type UnappliedFiltersByFacet,
} from 'bundles/search-common/components/experiments/filters2024/CheckableExpandableSearchFilterGroup';
import FilterChipDropdown from 'bundles/search-common/components/experiments/filters2024/FilterChipDropdown';
import { CourseraPlusFilterChipButton } from 'bundles/search-common/components/experiments/filters2024/components/CourseraPlusFilterChipButton';
import {
  FILTER_DROPDOWN_KEYS,
  getCountsPerKey,
  getFilterDropdownPropsMap,
  getFiltersByFacet,
  getSingleFilterValueForKey,
} from 'bundles/search-common/components/experiments/filters2024/utils';
import { useSearchFilters } from 'bundles/search-common/components/filters/useSearchFilters';
import { SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import {
  DEFAULT_SEARCH_FILTERS_SORTED_BY_USAGE,
  IS_PART_OF_COURSERA_PLUS,
} from 'bundles/search-common/constants/filter';
import { useShowCourseraTier } from 'bundles/search-common/providers/LearnerProvider';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import { type SearchFilterOption, type SearchPageEventingData } from 'bundles/search-common/types';
import { scrollUpIfNecessary } from 'bundles/search-common/utils/utils';

type Props = {
  eventingData: SearchPageEventingData;
  onOpenAllFiltersPanel?: (filterGroupKey?: string) => void;
  facets?: string[];
};

const styles = {
  chipWrapper: css`
    display: contents;

    ${breakpoints.down('sm')} {
      display: flex;
      align-items: center;
      gap: 2px;
      flex-wrap: nowrap;
    }
  `,
  chipDropdown: css`
    display: flex;
  `,
  desktopPanelWrapper: css`
    min-width: 360px;

    /* the goal is to have the total height of the panel + header + footer be around 416px by default  */
    max-height: 263px;
  `,
  checkboxGroup: css`
    width: 100%;
  `,
};

const scrollOnApply = () => {
  scrollUpIfNecessary({ mustBeScrolledPastThisElementId: 'search-results-header-wrapper' });
};

const getTriggerLabel = (
  filterGroupKey: string,
  filterGroupName: string,
  filterItems: SearchFilterOption[],
  countsPerCategory: Record<string, number>
): string | undefined => {
  if (countsPerCategory[filterGroupKey] !== undefined && countsPerCategory[filterGroupKey] < 3) {
    // Get labels of currently applied filters for this group
    const appliedFilterLabels = filterItems.filter((item) => item.isRefined).map((item) => item.label);

    if (appliedFilterLabels.length > 0) {
      return appliedFilterLabels.join(', ');
    }
  }
  return filterGroupName;
};

export const FilterChipDropdowns = (props: Props): React.ReactElement => {
  const { eventingData, onOpenAllFiltersPanel, facets } = props;
  const { setFiltersByFacet, getSearchResults, isLoading } = useSearch();
  const [unappliedFilters, setUnappliedFilters] = useState<UnappliedFiltersByFacet>();
  const isMobileOrTabletView = useMediaQuery(breakpoints.down('sm'));
  const shouldShowCourseraPlusFilter = useShowCourseraTier();
  const track = useTracker();

  const trackCtaClick = (groupName: string, buttonName: string) =>
    track('click_button', {
      button: {
        name: 'apply_search_filters',
      },
      carousel: {
        id: groupName,
        name: buttonName,
      },
    });

  const { facetFilters: currentFacetFilters } = getSearchResults(SEARCH_MAIN_INDEX_ID) || {};
  const dropdownKeys = facets ? facets.slice(0, 5) : FILTER_DROPDOWN_KEYS;

  const [persistentAdditionalKeys, setPersistentAdditionalKeys] = useState<string[]>([]);

  const currentAdditionalKeys = useMemo(() => {
    const appliedFilterKeys = (currentFacetFilters?.flat() || [])
      .map((filter) => filter.split(':')[0])
      .filter((key) => key && !dropdownKeys.includes(key) && key !== IS_PART_OF_COURSERA_PLUS);

    return DEFAULT_SEARCH_FILTERS_SORTED_BY_USAGE.filter((key) => appliedFilterKeys.includes(key));
  }, [currentFacetFilters, dropdownKeys]);

  useEffect(() => {
    if (!isLoading) {
      setPersistentAdditionalKeys((prev) => {
        const keysToKeep = prev.filter((key) => currentAdditionalKeys.includes(key));
        const newKeys = currentAdditionalKeys.filter((key) => !prev.includes(key));
        return [...keysToKeep, ...newKeys];
      });
    }
  }, [isLoading, currentAdditionalKeys]);

  const dynamicFacetOrder = useMemo(() => {
    const additionalKeys = isLoading ? persistentAdditionalKeys : currentAdditionalKeys;
    return [...(shouldShowCourseraPlusFilter ? [IS_PART_OF_COURSERA_PLUS] : []), ...additionalKeys, ...dropdownKeys];
  }, [shouldShowCourseraPlusFilter, currentAdditionalKeys, dropdownKeys, isLoading, persistentAdditionalKeys]);

  const { filterGroupConfigs, filterKeyToOptionsMap, facetFilters } = useSearchFilters({
    facetOrder: dynamicFacetOrder,
    useStableResults: true,
  });

  const filterKeyToValuesMap = filterKeyToOptionsMap;

  const countsPerCategory = useMemo(() => getCountsPerKey(facetFilters), [facetFilters]);
  const handleFiltersChange = (newFilters: UnappliedFiltersByFacet) => setUnappliedFilters(newFilters);

  const dropdownPropsMap = getFilterDropdownPropsMap();

  useEffect(() => {
    if (facetFilters) {
      setUnappliedFilters(getFiltersByFacet(facetFilters));
    }
  }, [facetFilters]);

  return (
    <div css={styles.chipWrapper}>
      {filterGroupConfigs.map((filterGroupConfig) => {
        const filterGroupKey = filterGroupConfig.property;
        const filterGroupName = dropdownPropsMap[filterGroupKey]?.triggerLabel ?? filterGroupConfig.getName();
        const hasCheckedFilters = unappliedFilters?.[filterGroupKey]?.length;

        if (filterGroupKey === IS_PART_OF_COURSERA_PLUS) {
          const isCPlusActive = getSingleFilterValueForKey(filterGroupKey, facetFilters) === 'true';
          return (
            <CourseraPlusFilterChipButton
              key={filterGroupKey}
              isActive={isCPlusActive}
              onClick={() => {
                setFiltersByFacet(
                  SEARCH_MAIN_INDEX_ID,
                  filterGroupKey,
                  isCPlusActive ? [] : [`${filterGroupKey}:true`]
                );
                scrollOnApply();
              }}
            />
          );
        }

        if (!filterKeyToValuesMap[filterGroupKey] || filterKeyToValuesMap[filterGroupKey].length === 0) {
          return null;
        }

        return (
          <div key={filterGroupKey} css={styles.chipDropdown}>
            <FilterChipDropdown
              panelHeader={
                <Typography2 variant="subtitleLarge" component="h3">
                  {filterGroupName}
                </Typography2>
              }
              triggerLabel={getTriggerLabel(
                filterGroupKey,
                filterGroupName,
                filterKeyToValuesMap[filterGroupKey] || [],
                countsPerCategory
              )}
              triggerCount={countsPerCategory[filterGroupKey] ?? 0}
              onReset={() => {
                setFiltersByFacet(SEARCH_MAIN_INDEX_ID, filterGroupKey, []);
                scrollOnApply();

                trackCtaClick(filterGroupName, 'reset');
              }}
              onApply={() => {
                scrollOnApply();

                trackCtaClick(filterGroupName, 'apply');
              }}
              shouldDisableCta={!hasCheckedFilters}
              onTriggerClick={
                isMobileOrTabletView && onOpenAllFiltersPanel ? () => onOpenAllFiltersPanel(filterGroupKey) : undefined
              }
            >
              <div css={[!isMobileOrTabletView && styles.desktopPanelWrapper]}>
                <CheckableExpandableSearchFilterGroup
                  groupConfig={{ ...filterGroupConfig, showAllItems: true }}
                  eventingData={eventingData}
                  groupValues={filterKeyToValuesMap[filterGroupKey] ?? []}
                  onFilterChange={handleFiltersChange}
                  unappliedFilters={unappliedFilters?.[filterGroupKey] ?? []}
                  isChip
                  shouldHideGroupLabel
                  shouldAutoApply
                />
              </div>
            </FilterChipDropdown>
          </div>
        );
      })}
    </div>
  );
};

export default FilterChipDropdowns;
